import { Data, Duration, Either, Match } from "effect";

/**
 * Duration literals (model v3.2 §4). A literal is a number plus exactly one of
 * five units — `s`, `m`, `h`, `d`, and `bd` (business days). No `ms`, no `w`.
 * `bd` is weekday arithmetic and carries no holiday calendar deliberately: a
 * holiday varies by counterparty and jurisdiction, so it is a harness
 * capability if ever needed, never a document literal.
 */
export const DURATION_UNITS = ["s", "m", "h", "d", "bd"] as const;

export type DurationUnit = (typeof DURATION_UNITS)[number];

export interface DurationLiteral {
  readonly value: number;
  readonly unit: DurationUnit;
}

/** Raised when a literal names a unit outside the fixed five, or is malformed. */
export class UnsupportedDurationLiteral extends Data.TaggedError("UnsupportedDurationLiteral")<{
  readonly literal: string;
}> {}

const LITERAL_PATTERN = /^(\d+)(bd|s|m|h|d)$/;

/** Parse a duration literal; `Left` for any unit outside the five, or a malformed literal. */
export const parseDurationLiteral = (
  literal: string,
): Either.Either<DurationLiteral, UnsupportedDurationLiteral> => {
  const match = LITERAL_PATTERN.exec(literal);
  if (match === null) {
    return Either.left(new UnsupportedDurationLiteral({ literal }));
  }
  return Either.right({ value: Number(match[1]), unit: match[2] as DurationUnit });
};

const DAY_MS = 86_400_000;

/** Whether a UTC instant falls on a weekday (Mon–Fri). */
const isWeekday = (millis: number): boolean => {
  const day = new Date(millis).getUTCDay();
  return day >= 1 && day <= 5;
};

/** Milliseconds from `fromMillis` to `n` weekdays forward (Mon–Fri), skipping weekends. */
const businessDaysMillis = (n: number, fromMillis: number): number => {
  let cursor = fromMillis;
  let remaining = n;
  while (remaining > 0) {
    cursor += DAY_MS;
    if (isWeekday(cursor)) {
      remaining -= 1;
    }
  }
  return cursor - fromMillis;
};

/**
 * Resolve a parsed literal to an Effect `Duration` anchored at `fromMillis`.
 * `bd` walks forward n weekdays from `fromMillis` and returns the difference;
 * the other four are fixed multiples independent of `fromMillis`.
 */
export const resolveDuration = (
  literal: DurationLiteral,
  fromMillis: number,
): Duration.Duration =>
  Match.value(literal.unit).pipe(
    Match.when("s", () => Duration.seconds(literal.value)),
    Match.when("m", () => Duration.minutes(literal.value)),
    Match.when("h", () => Duration.hours(literal.value)),
    Match.when("d", () => Duration.days(literal.value)),
    Match.when("bd", () => Duration.millis(businessDaysMillis(literal.value, fromMillis))),
    Match.exhaustive,
  );
